import { useState, useCallback, useEffect } from "react";
import { useTheme } from "../theme";
import { api } from "../api";

const API = process.env.REACT_APP_API_URL || "";

async function loadInventory(token) {
  const headers = { "Authorization": `Bearer ${token}` };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000);
  try {
    const res = await fetch(`${API}/inventory/items`, { headers, signal: controller.signal });
    clearTimeout(timeoutId);
    if (!res.ok) throw new Error("Failed to load inventory");
    return res.json();
  } catch (e) {
    clearTimeout(timeoutId);
    if (e.name === "AbortError") {
      throw new Error("Inventory scan is taking too long (45s+). This usually means one of your accounts' proxies is slow or unresponsive. Try again, or check your Proxy Pool.");
    }
    throw e;
  }
}

async function saveOverride(token, itemId, override) {
  const headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  const res = await fetch(`${API}/inventory/override`, { method: "POST", headers, body: JSON.stringify({ itemId, override }) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to save");
  }
}

export default function Inventory({ token, limits }) {
  const { accent, accentBg, accentBorder } = useTheme();
  const styles = getStyles(accent, accentBg, accentBorder);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [overrides, setOverrides] = useState({});
  const [floorPct, setFloorPct]   = useState("");
  const [sellQty, setSellQty]     = useState(null); // null = all copies
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState("");
  const [showUSD, setShowUSD]     = useState(false);
  const [usdSource, setUsdSource] = useState("routility"); // "routility" | "own"
  const [usdRate, setUsdRate]     = useState(2.7); // USD per 1,000 Robux, for "own" mode
  const [customUsdInput, setCustomUsdInput] = useState("");
  const [onlyValued, setOnlyValued] = useState(false);
  const [selectErr, setSelectErr] = useState("");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
  const [presets, setPresets] = useState([]);
  const [applyingPreset, setApplyingPreset] = useState("");
  const [presetMsg, setPresetMsg] = useState("");
  const [pctDraft, setPctDraft] = useState({});      // presetId -> draft percentage string
  const [dayDraft, setDayDraft] = useState({});      // presetId -> draft "days" string (hard_decay only)
  const [presetsCollapsed, setPresetsCollapsed] = useState(() => localStorage.getItem("rbxwar_presets_collapsed") === "true");
  const [customizing, setCustomizing] = useState(null); // presetId currently being customized, or null
  const [customizeExcluded, setCustomizeExcluded] = useState(new Set());

  const isProTier = !!limits?.autodiscover; // presets are Pro-only

  const refreshPresets = () => {
    if (!isProTier) return; // non-Pro never fetches presets at all
    api.listPresets(token).then(res => {
      const list = res.presets || [];
      setPresets(list);
      setPctDraft(prev => {
        const next = { ...prev };
        list.forEach(p => { if ((p.type === "fast_seller" || p.type === "hard_decay") && next[p.id] === undefined) next[p.id] = String(p.percentage); });
        return next;
      });
      setDayDraft(prev => {
        const next = { ...prev };
        list.forEach(p => { if (p.type === "hard_decay" && next[p.id] === undefined) next[p.id] = String(p.days); });
        return next;
      });
    }).catch(() => {});
  };

  useEffect(() => { refreshPresets(); }, [isProTier]);

  const applyPreset = async (preset, extra) => {
    setApplyingPreset(preset.id);
    setPresetMsg("");
    try {
      const res = await api.applyPreset(token, preset.id, extra);
      setPresetMsg(
        preset.type === "fast_seller"
          ? `✓ "${res.preset_name}" applied at ${res.percentage}%`
          : preset.type === "hard_decay"
          ? `✓ "${res.preset_name}" applied: ${res.percentage}% off value after ${res.days} day(s) unsold`
          : `✓ Applied "${res.preset_name}" to ${res.applied} item(s)`
      );
      refreshPresets();
      const data = await loadInventory(token);
      setOverrides(data.overrides || {});
    } catch (e) {
      setPresetMsg(`✗ ${e.message}`);
    } finally {
      setApplyingPreset("");
    }
  };

  const removePreset = async (preset) => {
    if (!window.confirm(`Remove "${preset.name}"? This undoes what it applied.`)) return;
    setApplyingPreset(preset.id);
    setPresetMsg("");
    try {
      await api.removePreset(token, preset.id);
      setPresetMsg(`Removed "${preset.name}"`);
      refreshPresets();
      const data = await loadInventory(token);
      setOverrides(data.overrides || {});
    } catch (e) {
      setPresetMsg(`✗ ${e.message}`);
    } finally {
      setApplyingPreset("");
    }
  };

  const togglePresetsCollapsed = () => {
    setPresetsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("rbxwar_presets_collapsed", next ? "true" : "false");
      return next;
    });
  };

  const openCustomize = (preset) => {
    setCustomizing(preset.id);
    setCustomizeExcluded(new Set(preset.items.filter(i => i.excluded).map(i => i.assetId)));
  };

  const saveCustomize = async (preset) => {
    await applyPreset(preset, { exclude_ids: Array.from(customizeExcluded) });
    setCustomizing(null);
  };
  const [showFloorPicker, setShowFloorPicker] = useState(false);
  const [floorSearch, setFloorSearch] = useState("");
  const [floorResults, setFloorResults] = useState([]);
  const [floorSearching, setFloorSearching] = useState(false);
  const [floorPickedItem, setFloorPickedItem] = useState(null);
  const [floorPickedPct, setFloorPickedPct] = useState("");
  const [floorPickedSaved, setFloorPickedSaved] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Debounced search-as-you-type against the full Rolimons catalog
  useEffect(() => {
    if (!floorSearch.trim()) { setFloorResults([]); return; }
    setFloorSearching(true);
    const t = setTimeout(() => {
      fetch(`${API}/items/search?q=${encodeURIComponent(floorSearch.trim())}`, {
        headers: { "Authorization": `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(data => setFloorResults(data.items || []))
        .catch(() => setFloorResults([]))
        .finally(() => setFloorSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [floorSearch]);

  const pickFloorItem = (item) => {
    setFloorPickedItem(item);
    setFloorPickedPct(overrides[item.assetId]?.floor_pct?.toString() || "");
    setFloorResults([]);
    setFloorSearch(item.name);
    setFloorPickedSaved(false);
  };

  const saveFloorPickedItem = async () => {
    if (!floorPickedItem) return;
    const prev = overrides[floorPickedItem.assetId] || {};
    const ov = { ...prev, name: floorPickedItem.name, floor_pct: parseInt(floorPickedPct) || 100 };
    await saveOverride(token, floorPickedItem.assetId, ov);
    setOverrides(o => ({ ...o, [floorPickedItem.assetId]: ov }));
    setFloorPickedSaved(true);
    setTimeout(() => setFloorPickedSaved(false), 2000);
  };

  const maxItems = limits?.maxItems ?? null; // null = unlimited (Pro)
  const needsManualSelect = maxItems !== null; // Lite/Basic — no autodiscover
  const selectedCount = Object.values(overrides).filter(o => o?.selected).length;

  const toggleSelected = async (item, e) => {
    e.stopPropagation();
    const ov = overrides[item.assetId] || {};
    const willSelect = !ov.selected;
    if (willSelect && maxItems !== null && selectedCount >= maxItems) {
      setSelectErr(`Your plan allows selecting up to ${maxItems} items. Upgrade for more`);
      setTimeout(() => setSelectErr(""), 3500);
      return;
    }
    const newOv = { ...ov, name: item.name, selected: willSelect };
    try {
      await saveOverride(token, item.assetId, newOv);
      setOverrides(prev => ({ ...prev, [item.assetId]: newOv }));
    } catch (err) {
      setSelectErr(err.message);
      setTimeout(() => setSelectErr(""), 3500);
    }
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const data = await loadInventory(token);
      setItems(data.items || []);
      setOverrides(data.overrides || {});
      if (data.warnings && data.warnings.length > 0) {
        setError(data.warnings.join(" • "));
      }
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectItem = (item) => {
    setSelected(item);
    const ov = overrides[item.assetId] || {};
    setFloorPct(ov.floor_pct !== undefined ? String(ov.floor_pct) : "");
    setSellQty(ov.sell_qty !== undefined ? ov.sell_qty : null);
    setCustomUsdInput(ov.customUsd !== undefined ? String(ov.customUsd) : "");
    setSaved(false);
  };

  const saveItemOverride = async (skip) => {
    if (!selected) return;
    const prev = overrides[selected.assetId] || {};
    const ov = skip
      ? { ...prev, name: selected.name, skip: true }
      : { ...prev, name: selected.name, floor_pct: parseInt(floorPct) || 100, skip: false, ...(sellQty !== null ? { sell_qty: sellQty } : {}) };
    await saveOverride(token, selected.assetId, ov);
    const newOv = { ...overrides, [selected.assetId]: ov };
    setOverrides(newOv);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveCustomUsd = async () => {
    if (!selected) return;
    const val = parseFloat(customUsdInput);
    if (isNaN(val) || val < 0) return;
    const prev = overrides[selected.assetId] || {};
    const ov = { ...prev, name: selected.name, customUsd: val };
    await saveOverride(token, selected.assetId, ov);
    setOverrides(prev => ({ ...prev, [selected.assetId]: ov }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const clearOverride = async () => {
    if (!selected) return;
    const headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
    await fetch(`${API}/inventory/override`, { method: "DELETE", headers, body: JSON.stringify({ itemId: selected.assetId }) });
    const newOv = { ...overrides };
    delete newOv[selected.assetId];
    setOverrides(newOv);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const getOv = (assetId) => overrides[assetId];

  const formatRobux = (n) => (n === null || n === undefined) ? "-" : n.toLocaleString();

  // Resolve the USD figure for an item under whichever source is active:
  // - "routility": real market price, falling back to a user-set custom
  //   USD override if RoUtility has no data for this item (e.g. Tix Bling)
  // - "own": the old formula, using the user's own rate per 1k Robux
  const resolveUsd = (item) => {
    const ov = getOv(item.assetId);
    if (usdSource === "own") {
      const base = item.value || item.rap;
      return base ? (base / 1000) * usdRate : null;
    }
    // routility mode
    if (item.usd != null) return item.usd;
    if (ov?.customUsd != null) return ov.customUsd;
    return null;
  };

  const formatBanner = (item) => {
    if (showUSD) {
      const usd = resolveUsd(item);
      if (usd != null) return `$${usd.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
      return null; // handled separately — shows a "set custom" prompt instead
    }
    return formatRobux(item.value || item.rap);
  };

  const displayItems = items
    .filter(item => !onlyValued || item.value)
    .slice()
    .sort((a, b) => (b.value || b.rap || 0) - (a.value || a.rap || 0));

  return (
    <div style={styles.wrap}>
      {!isProTier && (
        <div style={styles.presetsLockedBox}>
          <span style={{ fontSize: 13 }}>🔒</span>
          <span style={{ color: "#888", fontSize: 12.5 }}>
            <b style={{ color: "#ccc" }}>Presets</b> (curated floor/decay packs you can apply in one click) are a Pro feature. Upgrade to unlock them.
          </span>
        </div>
      )}

      {isProTier && presets.length > 0 && (
        <div style={styles.presetsBox}>
          <div style={styles.presetsHeader} onClick={togglePresetsCollapsed} title={presetsCollapsed ? "Expand" : "Collapse"}>
            <span style={styles.presetsTitle}>Presets</span>
            <span style={styles.betaTag}>BETA</span>
            <span style={{ marginLeft: "auto", color: "#555", fontSize: 14, transform: presetsCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>▾</span>
          </div>
          {!presetsCollapsed && (
            <>
              <div style={styles.presetsSub}>Apply a curated set of floor %/not-selling overrides to your items in one click.</div>
              <div style={styles.presetsList}>
                {presets.map(p => (
                  <div key={p.id} style={styles.presetCard}>
                    <div style={{ position: "relative" }}>
                      <img
                        src={p.image}
                        alt={p.name}
                        style={styles.presetImg}
                        onError={e => { e.target.style.display = "none"; }}
                      />
                      {p.badge === "new" && <span style={styles.newTag}>NEW</span>}
                    </div>
                    <div style={styles.presetName}>{p.name}</div>
                    <div style={styles.presetDesc}>{p.description}</div>
                    {p.type === "items" && (
                      <div style={styles.presetCount}>
                        {p.item_count}/{p.total_item_count} item{p.total_item_count === 1 ? "" : "s"}{p.applied ? " active" : ""}
                      </div>
                    )}

                    {p.type === "fast_seller" && (
                      <div style={styles.pctRow}>
                        <span style={styles.pctLabel}>Floor %</span>
                        <input
                          style={styles.pctInput}
                          type="number"
                          min={1} max={100}
                          value={pctDraft[p.id] ?? p.percentage}
                          onChange={e => setPctDraft(prev => ({ ...prev, [p.id]: e.target.value }))}
                        />
                      </div>
                    )}

                    {p.type === "hard_decay" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={styles.pctRow}>
                          <span style={styles.pctLabel}>Decay after</span>
                          <input
                            style={styles.pctInput}
                            type="number"
                            min={1} max={30}
                            value={dayDraft[p.id] ?? p.days}
                            onChange={e => setDayDraft(prev => ({ ...prev, [p.id]: e.target.value }))}
                          />
                          <span style={{ color: "#555", fontSize: 11 }}>day(s)</span>
                        </div>
                        <div style={styles.pctRow}>
                          <span style={styles.pctLabel}>Drop</span>
                          <input
                            style={styles.pctInput}
                            type="number"
                            min={1} max={50}
                            value={pctDraft[p.id] ?? p.percentage}
                            onChange={e => setPctDraft(prev => ({ ...prev, [p.id]: e.target.value }))}
                          />
                          <span style={{ color: "#555", fontSize: 11 }}>%/day of value</span>
                        </div>
                      </div>
                    )}

                    <div style={styles.presetBtnRow}>
                      {p.type === "placeholder" ? (
                        <button style={{...styles.presetBtn, background:"#222", color:"#666", cursor:"default"}} disabled>
                          Coming soon
                        </button>
                      ) : p.applied ? (
                        <>
                          <div style={styles.appliedTag}>✓ Applied</div>
                          {p.type === "fast_seller" && (
                            <button
                              style={styles.presetBtnSmall}
                              onClick={() => applyPreset(p, { percentage: parseInt(pctDraft[p.id]) || p.percentage })}
                              disabled={applyingPreset === p.id}
                            >
                              Update %
                            </button>
                          )}
                          {p.type === "hard_decay" && (
                            <button
                              style={styles.presetBtnSmall}
                              onClick={() => applyPreset(p, { percentage: parseFloat(pctDraft[p.id]) || p.percentage, days: parseInt(dayDraft[p.id]) || p.days })}
                              disabled={applyingPreset === p.id}
                            >
                              Update
                            </button>
                          )}
                          {p.type === "items" && (
                            <button style={styles.presetBtnSmall} onClick={() => openCustomize(p)}>
                              Customize
                            </button>
                          )}
                          <button
                            style={{...styles.presetBtnSmall, color:"#f87171", borderColor:"#7f1d1d"}}
                            onClick={() => removePreset(p)}
                            disabled={applyingPreset === p.id}
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <button
                          style={{...styles.presetBtn, ...(p.type === "items" && p.total_item_count === 0 ? {background:"#222", color:"#666", cursor:"default"} : {})}}
                          onClick={() => p.type === "fast_seller"
                            ? applyPreset(p, { percentage: parseInt(pctDraft[p.id]) || p.percentage })
                            : p.type === "hard_decay"
                            ? applyPreset(p, { percentage: parseFloat(pctDraft[p.id]) || p.percentage, days: parseInt(dayDraft[p.id]) || p.days })
                            : applyPreset(p, {})}
                          disabled={applyingPreset === p.id || (p.type === "items" && p.total_item_count === 0)}
                        >
                          {applyingPreset === p.id ? "Applying..." : (p.type === "items" && p.total_item_count === 0) ? "Coming soon" : "Apply"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {presetMsg && <div style={styles.presetMsg}>{presetMsg}</div>}
            </>
          )}

          {customizing && (() => {
            const preset = presets.find(p => p.id === customizing);
            if (!preset) return null;
            return (
              <div style={styles.customizeOverlay} onClick={() => setCustomizing(null)}>
                <div style={styles.customizeBox} onClick={e => e.stopPropagation()}>
                  <div style={styles.presetName}>Customize "{preset.name}"</div>
                  <div style={styles.presetDesc}>Uncheck any item you don't want included in this preset.</div>
                  <div style={styles.customizeList}>
                    {preset.items.map(it => {
                      const isExcluded = customizeExcluded.has(it.assetId);
                      return (
                        <label key={it.assetId} style={styles.customizeRow}>
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => {
                              setCustomizeExcluded(prev => {
                                const next = new Set(prev);
                                if (isExcluded) next.delete(it.assetId); else next.add(it.assetId);
                                return next;
                              });
                            }}
                          />
                          <span>Asset {it.assetId}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{display:"flex", gap:8, marginTop:12}}>
                    <button style={styles.presetBtn} onClick={() => saveCustomize(preset)}>Save</button>
                    <button style={{...styles.presetBtnSmall, flex:1}} onClick={() => setCustomizing(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {items.length > 0 && (
        <div style={styles.rescanBanner}>
          <span style={styles.rescanText}>Inventory doesn't look right? A fresh rescan pulls straight from Roblox again.</span>
          <button style={styles.rescanBtn} onClick={load} disabled={loading}>
            {loading ? "Rescanning..." : "↻ Rescan"}
          </button>
        </div>
      )}
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Inventory</h2>
          <div style={styles.sub}>Set floor % or mark items as not selling</div>
        </div>
        <div style={styles.headerRight}>
          <button
            style={{...styles.usdBtn, ...(showFloorPicker ? styles.usdBtnActive : {})}}
            onClick={() => setShowFloorPicker(v => !v)}
          >
            {showFloorPicker ? "✓ " : ""}Set Custom Floor %s
          </button>
          <button
            style={{...styles.usdBtn, ...(onlyValued ? styles.usdBtnActive : {})}}
            onClick={() => setOnlyValued(v => !v)}
          >
            {onlyValued ? "✓ " : ""}Show only valued items
          </button>
          <button
            style={{...styles.usdBtn, ...(showUSD ? styles.usdBtnActive : {})}}
            onClick={() => setShowUSD(v => !v)}
          >
            {showUSD ? "✓ " : ""}Show USD values
          </button>
          {showUSD && (
            <div style={styles.sourceGroup}>
              <button
                style={{...styles.sourceBtn, ...(usdSource === "routility" ? styles.sourceBtnActive : {})}}
                onClick={() => setUsdSource("routility")}
              >
                RoUtility
              </button>
              <button
                style={{...styles.sourceBtn, ...(usdSource === "own" ? styles.sourceBtnActive : {})}}
                onClick={() => setUsdSource("own")}
              >
                My Rate
              </button>
            </div>
          )}
          {showUSD && usdSource === "own" && (
            <div style={styles.rateWrap}>
              <span style={styles.rateLabel}>Rate</span>
              <span style={styles.rateDollar}>$</span>
              <input
                style={styles.rateInput}
                type="number"
                step="0.1"
                min="0"
                value={usdRate}
                onChange={e => setUsdRate(parseFloat(e.target.value) || 0)}
              />
              <span style={styles.rateLabel}>/ 1k</span>
            </div>
          )}
          <button style={styles.loadBtn} onClick={load} disabled={loading}>
            {loading ? "Loading..." : "↻ Load Inventory"}
          </button>
        </div>
      </div>
      {showFloorPicker && (
        <div style={styles.floorPickerBox}>
          <div style={styles.floorPickerLabel}>Set a floor % on any item, even ones you don't own yet. It'll be ready the moment you do</div>
          <div style={{position: "relative"}}>
            <input
              style={styles.floorSearchInput}
              placeholder="Search item name..."
              value={floorSearch}
              onChange={e => { setFloorSearch(e.target.value); setFloorPickedItem(null); }}
            />
            {floorSearch.trim() && !floorPickedItem && (
              <div style={styles.floorDropdown}>
                {floorSearching && <div style={styles.floorDropdownMsg}>Searching...</div>}
                {!floorSearching && floorResults.length === 0 && (
                  <div style={styles.floorDropdownMsg}>No items found</div>
                )}
                {floorResults.map(item => (
                  <div key={item.assetId} style={styles.floorDropdownRow} onClick={() => pickFloorItem(item)}>
                    {item.thumbnail && <img src={item.thumbnail} alt={item.name} style={styles.floorDropdownThumb} onError={e => e.target.style.display = "none"} />}
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={styles.floorDropdownName}>{item.name}</div>
                      <div style={styles.floorDropdownSub}>
                        RAP {item.rap != null ? item.rap.toLocaleString() : "-"}
                        {item.value != null && ` · Value ${item.value.toLocaleString()}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {floorPickedItem && (
            <div style={styles.floorPickedRow}>
              {floorPickedItem.thumbnail && <img src={floorPickedItem.thumbnail} alt={floorPickedItem.name} style={styles.floorDropdownThumb} onError={e => e.target.style.display = "none"} />}
              <div style={{flex: 1, minWidth: 0}}>
                <div style={styles.floorDropdownName}>{floorPickedItem.name}</div>
              </div>
              <input
                style={styles.numInput}
                type="number"
                min={1} max={200}
                placeholder="Floor %"
                value={floorPickedPct}
                onChange={e => setFloorPickedPct(e.target.value)}
              />
              <button style={styles.saveBtn} onClick={saveFloorPickedItem}>
                {floorPickedSaved ? "✓" : "Set"}
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}
      {selectErr && <div style={styles.error}>{selectErr}</div>}
      {needsManualSelect && (
        <div style={styles.selectBanner}>
          <span style={{color: selectedCount >= maxItems ? "#ef4444" : "#888"}}>
            {selectedCount} / {maxItems} items selected for pricewar
          </span>
          <span style={{color:"#444", fontSize:11}}> (click an item's star to select it, or click the card for more options)</span>
        </div>
      )}

      <div style={{...styles.body, ...(isMobile ? {flexDirection: "column", overflow: "visible"} : {})}}>
        {/* Item grid */}
        <div style={styles.grid}>
          {displayItems.length === 0 && !loading && (
            <div style={styles.empty}>
              {items.length === 0 ? 'Click "Load Inventory" to scan your items' : "No items match this filter"}
            </div>
          )}
          {displayItems.map(item => {
            const ov = getOv(item.assetId);
            const isSkipped = ov?.skip;
            const hasFloor  = ov && !ov.skip && ov.floor_pct != null;
            return (
              <div
                key={item.assetId}
                style={{
                  ...styles.card,
                  ...(selected?.assetId === item.assetId ? styles.cardSelected : {}),
                  ...(isSkipped ? styles.cardSkipped : {}),
                }}
                onClick={() => selectItem(item)}
              >
                {isSkipped && <div style={styles.skippedBadge}>NOT SELLING</div>}
                {hasFloor  && <div style={styles.floorBadge}>{ov.floor_pct}% floor</div>}
                {needsManualSelect && (
                  <button
                    style={{...styles.selectStar, ...(ov?.selected ? styles.selectStarActive : {})}}
                    onClick={(e) => toggleSelected(item, e)}
                    title={ov?.selected ? "Selected for pricewar (click to remove)" : "Select for pricewar"}
                  >
                    {ov?.selected ? "★" : "☆"}
                  </button>
                )}
                {item.ciiid && (
                  <a
                    href={`https://routility.io/ciiid/${item.ciiid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={styles.ciiidBtn}
                    title="View this item's instance history on RoUtility"
                  >
                    🔗
                  </a>
                )}
                <img
                  src={item.thumbnail || ""}
                  alt={item.name}
                  style={{...styles.thumb, ...(isSkipped ? {opacity:0.3} : {})}}
                  onError={e => e.target.style.display = "none"}
                />
                <div style={styles.cardBody}>
                  <div style={{...styles.itemName, ...(isSkipped ? {color:"#444"} : {})}} title={item.name}>
                    {(item.name || "Unknown Item").length > 22 ? (item.name || "Unknown Item").slice(0, 22) + "…" : (item.name || "Unknown Item")}
                  </div>
                  {showUSD ? (
                    formatBanner(item) ? (
                      <div style={styles.usdBanner}>{formatBanner(item)}</div>
                    ) : (
                      <div style={styles.noUsdBanner} onClick={(e) => { e.stopPropagation(); selectItem(item); }}>
                        No market data, set custom
                      </div>
                    )
                  ) : null}
                  <div style={styles.statRow}>
                    <span style={styles.statLabel}>RAP</span>
                    <span style={styles.statVal}>{formatRobux(item.rap)}</span>
                  </div>
                  {item.value && (
                    <div style={styles.statRow}>
                      <span style={styles.statLabel}>Value</span>
                      <span style={{...styles.statVal, color:"#f59e0b"}}>{formatRobux(item.value)}</span>
                    </div>
                  )}
                  {item.serial && (
                    <div style={styles.statRow}>
                      <span style={styles.statLabel}>Serial</span>
                      <span style={{...styles.statVal, color:"#f59e0b"}}>#{item.serial}</span>
                    </div>
                  )}
                  {item.qty > 1 && (
                    <div style={styles.qtyBadge}>x{item.qty}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Side panel */}
        {selected && (
          <div style={{...styles.panel, ...(isMobile ? {width: "100%"} : {})}}>
            <img src={selected.thumbnail} alt="" style={styles.panelThumb} onError={e=>e.target.style.display="none"} />
            <div style={styles.panelName}>{selected.name}</div>
            <div style={styles.panelStats}>
              {[
                ["RAP", formatRobux(selected.rap)],
                ["Value", selected.value ? formatRobux(selected.value) : "-"],
                ["Serial", selected.serial ? `#${selected.serial}` : "-"],
                ["Qty", selected.qty || 1],
              ].map(([k, v]) => (
                <div key={k} style={styles.panelStat}>
                  <span style={{color:"#555"}}>{k}</span>
                  <span style={{color:"#fff"}}>{v}</span>
                </div>
              ))}
            </div>

            <div style={styles.divider} />

            <div style={styles.panelLabel}>Override Settings</div>

            {selected.usd == null && (
              <div style={styles.fieldRow}>
                <label style={styles.fieldLabel}>Custom USD value <span style={{color:"#444"}}>(no RoUtility data for this item)</span></label>
                <div style={{display:"flex",gap:8}}>
                  <input
                    style={styles.numInput}
                    type="number"
                    min={0} step="0.01"
                    value={customUsdInput}
                    onChange={e => setCustomUsdInput(e.target.value)}
                    placeholder="e.g. 45.00"
                  />
                  <button style={styles.saveBtn} onClick={saveCustomUsd}>
                    {saved ? "✓" : "Set"}
                  </button>
                </div>
              </div>
            )}

            {/* Floor % */}
            <div style={styles.fieldRow}>
              <label style={styles.fieldLabel}>Floor %</label>
              <div style={{display:"flex",gap:8}}>
                <input
                  style={styles.numInput}
                  type="number"
                  min={1} max={200}
                  value={floorPct}
                  onChange={e => setFloorPct(e.target.value)}
                  placeholder="e.g. 90"
                />
                <button style={styles.saveBtn} onClick={() => saveItemOverride(false)}>
                  {saved ? "✓" : "Set"}
                </button>
              </div>
            </div>

            {/* Sell quantity — only shown when item has multiple copies */}
            {selected.qty > 1 && (
              <div style={styles.fieldRow}>
                <label style={styles.fieldLabel}>
                  Copies to sell
                  <span style={{color:"#555",fontWeight:400,marginLeft:6}}>{sellQty === null ? `all ${selected.qty}` : `${sellQty} of ${selected.qty}`}</span>
                </label>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <input
                    type="range"
                    min={1}
                    max={selected.qty}
                    value={sellQty ?? selected.qty}
                    onChange={e => setSellQty(parseInt(e.target.value))}
                    style={{width:"100%",accentColor:"var(--rbx-accent,#38bdf8)"}}
                  />
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{display:"flex",gap:6}}>
                      {Array.from({length:selected.qty},(_,i)=>i+1).map(n=>(
                        <button key={n}
                          onClick={()=>setSellQty(n)}
                          style={{padding:"3px 8px",borderRadius:5,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
                            background:sellQty===n?"var(--rbx-accent,#38bdf8)":"#1a1a1a",
                            color:sellQty===n?"#000":"#666"}}
                        >{n}</button>
                      ))}
                    </div>
                    <button
                      onClick={()=>setSellQty(null)}
                      style={{padding:"3px 8px",borderRadius:5,border:"none",cursor:"pointer",fontSize:11,
                        background:sellQty===null?"#2a2a2a":"#111",color:sellQty===null?"#ccc":"#555"}}
                    >all</button>
                  </div>
                  <button style={styles.saveBtn} onClick={() => saveItemOverride(false)}>{saved?"✓ Saved":"Save"}</button>
                </div>
              </div>
            )}

            <button
              style={{...styles.skipBtn, ...(getOv(selected.assetId)?.skip ? styles.skipBtnActive : {})}}
              onClick={() => getOv(selected.assetId)?.skip ? clearOverride() : saveItemOverride(true)}
            >
              {getOv(selected.assetId)?.skip ? "✓ Not Selling (click to re-enable)" : "Mark as Not Selling"}
            </button>

            {getOv(selected.assetId) && !getOv(selected.assetId)?.skip && (
              <button style={styles.clearBtn} onClick={clearOverride}>Clear Override</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const getStyles = (accent, accentBg, accentBorder) => ({
  wrap:          { minHeight:"100%", display:"flex", flexDirection:"column", overflowY:"auto" },
  rescanBanner:  { display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:"10px 14px", marginBottom:14 },
  presetsBox:    { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10, padding:16, marginBottom:14 },
  presetsHeader: { display:"flex", alignItems:"center", gap:8, marginBottom:4, cursor:"pointer", userSelect:"none" },
  newTag:        { position:"absolute", top:6, right:6, fontSize:9, fontWeight:800, letterSpacing:0.5, color:"#000", background:"var(--rbx-accent, #38bdf8)", borderRadius:4, padding:"2px 6px" },
  presetsLockedBox: { display:"flex", alignItems:"center", gap:10, background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10, padding:"12px 16px", marginBottom:14 },
  presetsTitle:  { color:"#fff", fontSize:14, fontWeight:700 },
  betaTag:       { fontSize:9, letterSpacing:0.5, color:"#f59e0b", border:"1px solid #f59e0b55", background:"#f59e0b15", borderRadius:4, padding:"2px 6px" },
  presetsSub:    { color:"#666", fontSize:12, marginBottom:12 },
  presetsList:   { display:"flex", flexWrap:"wrap", gap:10 },
  presetCard:    { flex:"1 1 160px", minWidth:160, maxWidth:200, background:"#141414", border:"1px solid #222", borderRadius:8, padding:12 },
  presetImg:     { width:"100%", aspectRatio:"1", objectFit:"contain", background:"#111", display:"block", borderRadius:6, marginBottom:8 },
  presetName:    { color:"#fff", fontSize:13, fontWeight:700, marginBottom:4 },
  presetDesc:    { color:"#666", fontSize:11.5, lineHeight:1.4, marginBottom:8, minHeight:32 },
  presetCount:   { color:"#555", fontSize:10.5, marginBottom:10 },
  presetBtn:     { width:"100%", padding:"7px 0", background:accent, border:"none", borderRadius:6, color:"#000", fontWeight:700, fontSize:12, cursor:"pointer" },
  presetBtnRow:  { display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" },
  presetBtnSmall:{ flex:1, padding:"6px 8px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, color:"#ccc", fontWeight:600, fontSize:11, cursor:"pointer" },
  appliedTag:    { color:accent, fontSize:11.5, fontWeight:700, whiteSpace:"nowrap" },
  pctRow:        { display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:10, background:"#0f0f0f", border:"1px solid #222", borderRadius:6, padding:"6px 8px" },
  pctLabel:      { color:"#666", fontSize:11 },
  pctInput:      { width:50, background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:4, padding:"3px 6px", color:"#fff", fontSize:12, outline:"none", textAlign:"right" },
  presetMsg:     { marginTop:10, fontSize:12, color:"#888" },
  customizeOverlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:50, display:"flex", alignItems:"center", justifyContent:"center" },
  customizeBox:  { width:360, maxWidth:"calc(90vw / var(--ui-zoom, 1))", maxHeight:"calc(80vh / var(--ui-zoom, 1))", overflowY:"auto", background:"#141414", border:"1px solid #2a2a2a", borderRadius:10, padding:18 },
  customizeList: { display:"flex", flexDirection:"column", gap:8, marginTop:12, maxHeight:260, overflowY:"auto" },
  customizeRow:  { display:"flex", alignItems:"center", gap:8, color:"#ccc", fontSize:12.5, cursor:"pointer" },
  rescanText:    { color:"#666", fontSize:12 },
  rescanBtn:     { padding:"6px 14px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, color:"#ccc", fontWeight:600, cursor:"pointer", fontSize:12, whiteSpace:"nowrap" },
  selectBanner:  { marginBottom:12, fontSize:12, padding:"8px 12px", background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8 },
  selectStar:    { position:"absolute", bottom:6, left:6, zIndex:2, width:24, height:24, borderRadius:6, background:"#1a1a1a", border:"1px solid #2a2a2a", color:"#555", fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, transition:"all .15s" },
  selectStarActive: { background:"#f59e0b22", border:"1px solid #f59e0b", color:"#f59e0b" },
  ciiidBtn:      { position:"absolute", bottom:6, right:6, zIndex:2, width:22, height:22, borderRadius:6, background:"#1a1a1a", border:"1px solid #2a2a2a", color:"#888", fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, textDecoration:"none", transition:"all .15s" },
  header:        { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:12 },
  headerRight:   { display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" },
  usdBtn:        { padding:"10px 16px", background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, color:"#888", cursor:"pointer", fontSize:13, fontWeight:600, transition:"all .15s" },
  usdBtnActive:  { background:"rgba(var(--rbx-accent-rgb,56,189,248),0.13)", border:"1px solid var(--rbx-accent,#38bdf8)", color:accent },
  sourceGroup:   { display:"flex", background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, padding:2, gap:2 },
  sourceBtn:     { padding:"7px 12px", background:"transparent", border:"none", borderRadius:6, color:"#666", cursor:"pointer", fontSize:12, fontWeight:600, transition:"all .15s" },
  sourceBtnActive: { background:"#1a1a1a", color:accent },
  rateWrap:      { display:"flex", alignItems:"center", gap:6, background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:8, padding:"6px 12px" },
  rateLabel:     { color:"#555", fontSize:12 },
  rateDollar:    { color:"#666", fontSize:13 },
  rateInput:     { width:50, background:"transparent", border:"none", color:"#fff", fontSize:13, outline:"none" },
  noUsdBanner:   { color:"#666", fontSize:11, marginBottom:8, cursor:"pointer", textDecoration:"underline", textDecorationStyle:"dotted" },
  title:         { color:"#fff", margin:0, fontSize:18 },
  sub:           { color:"#555", fontSize:12, marginTop:4 },
  loadBtn:       { padding:"10px 18px", background:accent, border:"none", borderRadius:8, color:"#000", fontWeight:700, cursor:"pointer", fontSize:13 },
  error:         { color:"#f44", marginBottom:12, fontSize:13 },
  body:          { display:"flex", gap:16, flex:1 },
  grid:          { flex:1, minWidth:0, display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10, alignContent:"start" },
  empty:         { color:"#333", padding:"60px 20px", textAlign:"center", gridColumn:"1/-1", fontSize:14 },
  card:          { background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10, cursor:"pointer", transition:"border .15s", position:"relative", overflow:"hidden" },
  cardSelected:  { border:"1px solid var(--rbx-accent,#38bdf8)" },
  cardSkipped:   { border:"1px solid #2a2a2a" },
  skippedBadge:  { position:"absolute", top:6, left:6, background:"#1a1a1a", color:"#555", fontSize:9, padding:"2px 6px", borderRadius:4, letterSpacing:1, zIndex:1 },
  floorBadge:    { position:"absolute", top:6, right:6, background:"rgba(var(--rbx-accent-rgb,56,189,248),0.13)", color:"var(--rbx-accent,#38bdf8)", fontSize:9, padding:"2px 6px", borderRadius:4, letterSpacing:1, zIndex:1 },
  thumb:         { width:"100%", aspectRatio:"1", objectFit:"contain", background:"#111", display:"block" },
  cardBody:      { padding:"10px 10px 12px", position:"relative" },
  itemName:      { color:"#ccc", fontSize:12, fontWeight:600, marginBottom:6, lineHeight:1.3 },
  usdBanner:     { color:accent, fontSize:20, fontWeight:800, marginBottom:8, lineHeight:1.1, fontFamily:"'Rubik', sans-serif" },
  statRow:       { display:"flex", justifyContent:"space-between", marginBottom:2 },
  statLabel:     { color:"#444", fontSize:11 },
  statVal:       { color:"#888", fontSize:11 },
  qtyBadge:      { position:"absolute", bottom:8, right:8, background:"#1a1a1a", color:"#666", fontSize:10, padding:"1px 6px", borderRadius:4 },
  panel:         { width:220, background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10, padding:16, flexShrink:0, overflowY:"auto" },
  panelThumb:    { width:"100%", aspectRatio:"1", objectFit:"contain", background:"#111", borderRadius:8, marginBottom:12, display:"block" },
  panelName:     { color:"#fff", fontSize:14, fontWeight:700, marginBottom:12, lineHeight:1.3 },
  panelStats:    { display:"flex", flexDirection:"column", gap:6, marginBottom:12 },
  panelStat:     { display:"flex", justifyContent:"space-between", fontSize:12 },
  divider:       { borderTop:"1px solid #1a1a1a", margin:"12px 0" },
  panelLabel:    { color:"#555", fontSize:11, letterSpacing:1, marginBottom:10 },
  fieldRow:      { marginBottom:12 },
  fieldLabel:    { display:"block", color:"#666", fontSize:12, marginBottom:6 },
  numInput:      { width:80, background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:6, padding:"6px 10px", color:"#fff", fontSize:13, outline:"none" },
  saveBtn:       { padding:"6px 14px", background:accent, border:"none", borderRadius:6, color:"#000", fontWeight:700, cursor:"pointer", fontSize:13 },
  floorPickerBox:{ background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10, padding:16, marginBottom:16 },
  floorPickerLabel:{ color:"#666", fontSize:12, marginBottom:10 },
  floorSearchInput:{ width:"100%", maxWidth:400, background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box" },
  floorDropdown: { position:"absolute", top:"calc(100% + 4px)", left:0, width:"100%", maxWidth:400, maxHeight:280, overflowY:"auto", background:"#161616", border:"1px solid #2a2a2a", borderRadius:8, zIndex:20, boxShadow:"0 12px 32px rgba(0,0,0,0.5)" },
  floorDropdownMsg:{ color:"#555", fontSize:12.5, padding:12 },
  floorDropdownRow:{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", cursor:"pointer", borderBottom:"1px solid #1f1f1f" },
  floorDropdownThumb:{ width:32, height:32, objectFit:"contain", background:"#0f0f0f", borderRadius:6, flexShrink:0 },
  floorDropdownName:{ color:"#ddd", fontSize:12.5, fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  floorDropdownSub:{ color:"#555", fontSize:11 },
  floorPickedRow:{ display:"flex", alignItems:"center", gap:10, marginTop:12, background:"#111", border:"1px solid #1a1a1a", borderRadius:8, padding:10 },
  skipBtn:       { width:"100%", padding:"9px 0", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:8, color:"#888", cursor:"pointer", fontSize:12, marginBottom:8 },
  skipBtnActive: { background:"#7f1d1d22", border:"1px solid #7f1d1d", color:"#fca5a5" },
  clearBtn:      { width:"100%", padding:"7px 0", background:"none", border:"1px solid #222", borderRadius:8, color:"#555", cursor:"pointer", fontSize:12 },
});